import { CircularProgress, IconButton } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import type { FC, MouseEventHandler } from 'react';
import { useState } from 'react';

import { useMarketplaceContract } from '../../../../hooks/marketplace-contract.hooks';
import type { Listing } from '../../../../lib/graph/MogulMarketplace';
import { AssetType, ListingType } from '../../../../lib/graph/MogulMarketplace';
import { Button } from '../../../core/button';
import { Panel } from '../../panel/Panel.component';
import styles from './CancelNftListingModal.module.css';

export interface CancelNftListingModalProps {
  listing: Listing;
}
export interface CancelNftListingProps extends CancelNftListingModalProps {
  hideModal: () => void;
}

export interface CancelNftListingFormValues {
  newStarsListingPrice?: number;
  newEthListingPrice?: number;
  toAddress?: string;
}

const CancelNftListing: FC<CancelNftListingProps> = ({
  listing,
  hideModal,
}) => {
  const marketplaceContract = useMarketplaceContract({
    type: listing.asset.type ?? AssetType.Erc1155,
  });

  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) {
      return;
    }

    hideModal();
  };

  const handleSubmit: MouseEventHandler<HTMLButtonElement> = async event => {
    event.preventDefault();

    if (submitting || !marketplaceContract.initialized) {
      return;
    }

    setSubmitting(true);

    try {
      if (listing.type === ListingType.Listing) {
        await marketplaceContract.cancelListing(listing.listingId as string);
      } else {
        await marketplaceContract.cancelAuction(listing.listingId as string);
      }
    } catch (error) {
      setSubmitting(false);
      throw error;
    }

    setSubmitting(false);

    handleClose();
  };

  return (
    <Panel
      className="overflow-hidden"
      bodyClassName={styles.panel + ' flex flex-col rounded-lg m-8'}
    >
      <div className="flex flex-row">
        <div
          className={styles.modifyNFTListingPriceInfo + ' flex flex-col m-8'}
        >
          <div className="flex flex-col">
            <div className={styles.title + ' text-center py-4'}>
              Cancel{' '}
              {listing.type === ListingType.Listing ? 'Listing' : 'Auction'}?
            </div>
            <p className={styles.sellerEthAddress + ' text-center pb-6'}>
              This will return the Item back to you
            </p>
            <div className={'flex flex-row pt-6'}>
              <Button
                className="mr-4"
                type="submit"
                kind="secondary"
                fullWidth
                onClick={hideModal}
                disabled={!marketplaceContract.initialized || submitting}
              >
                No
              </Button>
              <Button
                type="submit"
                fullWidth
                onClick={handleSubmit}
                disabled={!marketplaceContract.initialized || submitting}
              >
                {!submitting ? (
                  'Yes'
                ) : (
                  <>
                    Canceling
                    <CircularProgress
                      className={'placeholder-white::placeholder ml-4'}
                      size={20}
                    />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        <div className="ml-4 relative">
          <IconButton
            className={'h-10 w-10 top-2 right-2 z-30'}
            style={{
              background: '#000000',
              border: 'none',
              position: 'absolute',
            }}
            onClick={hideModal}
          >
            <ClearIcon style={{ color: 'white' }} />
          </IconButton>
        </div>
      </div>
    </Panel>
  );
};
export const CancelNftListingModal =
  (props: CancelNftListingModalProps) =>
  ({ hideModal }: CancelNftListingProps) => {
    return <CancelNftListing hideModal={hideModal} {...props} />;
  };
